import { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '../auth/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth endpoints to avoid infinite loops and unwanted redirects
  if (
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/logout') ||
    req.url.includes('/auth/forgot-password') ||
    req.url.includes('/auth/validate-token') ||
    req.url.includes('/auth/set-password') ||
    req.url.includes('/auth/me')
  ) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handle401Error(req, next, authService, router);
      }

      return throwError(() => error);
    }),
  );
};

function handle401Error(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((success) => {
        if (success) {
          // Emit to waiting requests BEFORE resetting flag to avoid race condition
          refreshTokenSubject.next(true);
          isRefreshing = false;

          // Retry the original request
          return next(req);
        } else {
          // Refresh failed, redirect to login
          refreshTokenSubject.next(false);
          isRefreshing = false;
          router.navigate(['/auth/login']);

          return throwError(() => new Error('Session expired'));
        }
      }),
      catchError((err) => {
        refreshTokenSubject.next(false);
        isRefreshing = false;
        router.navigate(['/auth/login']);

        return throwError(() => err);
      }),
    );
  } else {
    // Wait for the ongoing refresh to complete
    return refreshTokenSubject.pipe(
      filter((result) => result !== null),
      take(1),
      switchMap((success) => {
        if (success) {
          return next(req);
        } else {
          return throwError(() => new Error('Session expired'));
        }
      }),
    );
  }
}
